// src/components/AreaCalculator.jsx
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import AOS from 'aos';

const Box = styled.div`
  background: ${({theme})=>theme.surface};
  border: 1px solid ${({theme})=>theme.border};
  border-radius: ${({theme})=>theme.radius.lg}px;
  box-shadow: ${({theme})=>theme.shadow.sm};
  padding: 20px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1.2fr .9fr .9fr auto;
  gap: 10px;
  align-items: center;
  @media (max-width: 720px){
    grid-template-columns: 1fr 1fr 1fr;
    grid-auto-rows: auto;
    > *:nth-child(4n+5){ grid-column: 1 / -1; }
  }
`;

const Grid = styled.div`
  display: grid; gap: 10px;
`;

const Small = styled.small`
  color: ${({theme})=>theme.textMuted};
`;

const Pills = styled.div`
  display:flex; flex-wrap:wrap; gap:8px;
  button {
    padding: 8px 12px; border-radius: ${({theme})=>theme.radius.pill}px; cursor: pointer;
    background: ${({theme})=>theme.surface}; color: ${({theme})=>theme.text};
    border: 1px solid ${({theme})=>theme.border}; font-weight: 600; font-size: 14px;
  }
  button.active {
    background: ${({theme})=>theme.body};
  }
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid ${({theme})=>theme.border};
  background: ${({theme})=>theme.surface};
  color: ${({theme})=>theme.text};
  border-radius: ${({theme})=>theme.radius.md}px;
  padding: 10px 12px;
  font-size: 16px;
  outline: none;
  &:focus{ border-color: ${({theme})=>theme.textMuted}; }
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid ${({theme})=>theme.border};
  background: ${({theme})=>theme.surface};
  color: ${({theme})=>theme.text};
  border-radius: ${({theme})=>theme.radius.md}px;
  padding: 10px 12px;
  font-size: 16px;
  outline: none;
  &:focus{ border-color: ${({theme})=>theme.textMuted}; }
`;

const GhostBtn = styled.button`
  padding: 10px 12px; border-radius: ${({theme})=>theme.radius.pill}px; cursor: pointer;
  background: transparent; color: ${({theme})=>theme.text};
  border: 1px solid ${({theme})=>theme.border};
`;

const PrimaryBtn = styled.button`
  padding: 10px 14px; border-radius: ${({theme})=>theme.radius.pill}px; cursor: pointer;
  background: ${({theme})=>theme.accent}; color: #111; border: 1px solid ${({theme})=>theme.accent};
  font-weight: 800;
`;

const Line = styled.div`
  height: 1px; background: ${({theme})=>theme.border}; margin: 8px 0 14px;
`;

const Result = styled.div`
  display: grid; gap: 8px;
  grid-template-columns: repeat(3, 1fr);
  @media (max-width: 720px){ grid-template-columns: 1fr; }
  .card{
    background: ${({theme})=>theme.surface};
    border: 1px solid ${({theme})=>theme.border};
    border-radius: ${({theme})=>theme.radius.md}px;
    padding: 12px;
  }
  .num{ font-size: 22px; font-weight: 800; }
`;

const Trash = styled.button`
  border: 1px solid ${({theme})=>theme.border};
  background: ${({theme})=>theme.surface};
  color: ${({theme})=>theme.text};
  padding: 8px 10px;
  border-radius: ${({theme})=>theme.radius.pill}px;
  cursor: pointer;
`;

const Help = styled.details`
  margin-top: 10px;
  summary{ cursor: pointer; }
  p{ margin: 6px 0 0; color: ${({theme})=>theme.textMuted}; }
`;

const sheetPresets = [
  { label: '3050 × 1300 мм (типовий HPL)', w: 3050, h: 1300 },
  { label: '2800 × 2070 мм', w: 2800, h: 2070 },
  { label: '2440 × 1220 мм', w: 2440, h: 1220 },
];

const unitOptions = [
  { key: 'mm', label: 'мм' },
  { key: 'm',  label: 'м'  },
];

function mmOrMToMM(value, unit){
  const v = Number(value)||0;
  return unit === 'm' ? v * 1000 : v;
}

function areaMM2ToM2(areaMM2){
  return areaMM2 / 1_000_000;
}

export default function AreaCalculator(){
  const [rows, setRows] = useState([
    { w: '', h: '', qty: 1, unit: 'mm' },
  ]);
  const [waste, setWaste] = useState(10); // %
  const [sheet, setSheet] = useState(sheetPresets[0]); // default 3050×1300
  const [customSheet, setCustomSheet] = useState({ w: sheetPresets[0].w, h: sheetPresets[0].h, unit: 'mm' });
  const [sheetMode, setSheetMode] = useState('preset'); // 'preset' | 'custom'

  // Инициализируем AOS, если компонент рендерится отдельно
  AOS.init?.({ duration: 600, once: true, offset: 80, easing: 'ease-out-cubic' });

  const totals = useMemo(()=>{
    // суммарная площадь по строкам
    let sumMM2 = 0;
    rows.forEach(r=>{
      const wMM = mmOrMToMM(r.w, r.unit || 'mm');
      const hMM = mmOrMToMM(r.h, r.unit || 'mm');
      const qty = Number(r.qty)||0;
      if (wMM>0 && hMM>0 && qty>0){
        sumMM2 += (wMM * hMM) * qty;
      }
    });
    const baseM2 = areaMM2ToM2(sumMM2);
    const totalWithWaste = baseM2 * (1 + (Number(waste)||0)/100);

    // площадь листа
    const sw = sheetMode==='preset' ? sheet.w : mmOrMToMM(customSheet.w, customSheet.unit);
    const sh = sheetMode==='preset' ? sheet.h : mmOrMToMM(customSheet.h, customSheet.unit);
    const sheetArea = areaMM2ToM2(sw * sh);

    const sheetsNeeded = sheetArea > 0 ? Math.ceil(totalWithWaste / sheetArea) : 0;

    return {
      baseM2: +(baseM2.toFixed(3)),
      totalWithWaste: +(totalWithWaste.toFixed(3)),
      sheetArea: +(sheetArea.toFixed(3)),
      sheetsNeeded
    };
  }, [rows, waste, sheet, customSheet, sheetMode]);

  const updateRow = (idx, patch) => {
    setRows(prev => prev.map((r,i)=> i===idx ? {...r, ...patch} : r));
  };

  const addRow = () => setRows(prev => [...prev, { w:'', h:'', qty:1, unit: prev.at(-1)?.unit || 'mm' }]);

  const removeRow = (idx) => setRows(prev => prev.filter((_,i)=> i!==idx));

  const resetRows = () => setRows([{ w:'', h:'', qty:1, unit:'mm' }]);

  return (
    <Box data-aos="zoom-in">
      <h3 style={{marginTop:0, marginBottom:6}}>Калькулятор площі</h3>
      <Small>Порахуйте сумарну площу заготовок і орієнтовну кількість листів HPL під популярні формати.</Small>

      <Line />

      {/* строки */}
      <Grid>
        {rows.map((r, idx)=>(
          <Row key={idx}>
            <div>
              <Small>Ширина</Small>
              <Input
                type="number" min="0" step="0.001"
                placeholder={r.unit==='mm' ? 'мм' : 'м'}
                value={r.w}
                onChange={e=>updateRow(idx, { w: e.target.value })}
              />
            </div>
            <div>
              <Small>Висота</Small>
              <Input
                type="number" min="0" step="0.001"
                placeholder={r.unit==='mm' ? 'мм' : 'м'}
                value={r.h}
                onChange={e=>updateRow(idx, { h: e.target.value })}
              />
            </div>
            <div>
              <Small>К-сть</Small>
              <Input
                type="number" min="0" step="1"
                value={r.qty}
                onChange={e=>updateRow(idx, { qty: e.target.value })}
              />
            </div>
            <div>
              <Small>Одиниці</Small>
              <Select
                value={r.unit}
                onChange={e=>updateRow(idx, { unit: e.target.value })}
              >
                {unitOptions.map(u=> <option key={u.key} value={u.key}>{u.label}</option>)}
              </Select>
            </div>
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              {rows.length>1 && (
                <Trash aria-label="Видалити рядок" onClick={()=>removeRow(idx)}>🗑️</Trash>
              )}
            </div>
          </Row>
        ))}
      </Grid>

      <div style={{display:'flex', gap:10, marginTop:10, flexWrap:'wrap'}}>
        <GhostBtn onClick={addRow}>＋ Додати рядок</GhostBtn>
        <GhostBtn onClick={resetRows}>↺ Очистити</GhostBtn>
      </div>

      <Line />

      {/* запас + формати листа */}
      <div style={{display:'grid', gap:12, gridTemplateColumns:'1fr 1fr', alignItems:'start', marginBottom:8}}>
        <div>
          <Small>Запас на відходи, %</Small>
          <Input
            type="number" min="0" step="1"
            value={waste}
            onChange={e=>setWaste(e.target.value)}
          />
          <Help>
            <summary>Підказка</summary>
            <p>Рекомендуємо 5–15% запас залежно від складності розкрою та кількості дрібних деталей.</p>
          </Help>
        </div>

        <div>
          <Small>Формат листа</Small>
          <Pills style={{marginBottom:8}}>
            <button
              className={sheetMode==='preset' ? 'active' : ''}
              onClick={()=>setSheetMode('preset')}
              type="button"
            >Готові</button>
            <button
              className={sheetMode==='custom' ? 'active' : ''}
              onClick={()=>setSheetMode('custom')}
              type="button"
            >Свій розмір</button>
          </Pills>

          {sheetMode==='preset' ? (
            <Select
              value={sheet.label}
              onChange={(e)=>{
                const found = sheetPresets.find(p=>p.label===e.target.value) || sheetPresets[0];
                setSheet(found);
              }}
            >
              {sheetPresets.map(p=>(
                <option key={p.label} value={p.label}>{p.label}</option>
              ))}
            </Select>
          ) : (
            <div style={{display:'grid', gap:8, gridTemplateColumns:'1fr 1fr auto'}}>
              <Input
                type="number" min="0" placeholder="Ширина"
                value={customSheet.w}
                onChange={e=>setCustomSheet(cs=>({...cs, w: e.target.value}))}
              />
              <Input
                type="number" min="0" placeholder="Висота"
                value={customSheet.h}
                onChange={e=>setCustomSheet(cs=>({...cs, h: e.target.value}))}
              />
              <Select
                value={customSheet.unit}
                onChange={e=>setCustomSheet(cs=>({...cs, unit: e.target.value}))}
              >
                {unitOptions.map(u=> <option key={u.key} value={u.key}>{u.label}</option>)}
              </Select>
            </div>
          )}
          <Small>Площа 1 листа: <b>{totals.sheetArea}</b> м²</Small>
        </div>
      </div>

      {/* результат */}
      <Result>
        <div className="card">
          <Small>Сума без запасу</Small>
          <div className="num">{totals.baseM2} м²</div>
        </div>
        <div className="card">
          <Small>З урахуванням запасу</Small>
          <div className="num">{totals.totalWithWaste} м²</div>
        </div>
        <div className="card">
          <Small>Необхідно листів (≈)</Small>
          <div className="num">{totals.sheetsNeeded}</div>
        </div>
      </Result>

      <div style={{display:'flex', gap:8, marginTop:12, flexWrap:'wrap'}}>
        <PrimaryBtn
          type="button"
          onClick={()=>{
            const text = `Потрібен прорахунок HPL:\n— Площа (без/із запасом): ${totals.baseM2} / ${totals.totalWithWaste} м²\n— Формат листа: ${sheetMode==='preset' ? sheet.label : `${customSheet.w}×${customSheet.h} ${customSheet.unit}`}\n— Орієнтовно листів: ${totals.sheetsNeeded}\n\nДеталізацію надішлю у відповідь.`;
            const mailto = `mailto:info@momv.com.ua?subject=${encodeURIComponent('Прорахунок HPL')}&body=${encodeURIComponent(text)}`;
            window.location.href = mailto;
          }}
        >Надіслати прорахунок на email</PrimaryBtn>
        <GhostBtn as="a" href="/contacts">Зв’язатися з нами</GhostBtn>
      </div>
    </Box>
  );
}
